import type { Filter, Query } from '@cubejs-client/core'

import {
  currencyMembers,
  type AgingWidget,
  type ChartWidget,
  type DashboardWidget,
  type KpiWidget,
  type ListWidget,
  type ReportSortItem,
  type TableWidget,
} from '@/lib/cube-report'
import { buildCubeQuery } from '@/lib/cube'
import { selectionFilters } from '@/lib/cross-filter'
import {
  buildColumnFilters,
  buildDefinedFilters,
  buildTableOrder,
  EXPORT_ROW_LIMIT,
  type ReportFilterState,
} from '@/services/cube/report'

/*
 * Pano parçalarının sorguları. Her parça kendi view'ını sorgular; sayfa
 * filtrelerinden yalnızca o view'da karşılığı olanlar uygulanır (bkz.
 * `parseWidgets`). Para birimini satırdan okuyan alanların döviz üyesi sorguya
 * eklenir; toplamlar döviz başına ayrı satır döner (TL ile USD toplanmaz).
 */

export function widgetFilters(
  widget: DashboardWidget,
  state: ReportFilterState,
  { skipDateRange = false }: { skipDateRange?: boolean } = {},
): Filter[] {
  const filters = buildDefinedFilters(widget.filters, state, { skipDateRange })
  if (widget.type === 'table' || widget.type === 'list') {
    for (const { member, values } of widget.where ?? []) {
      filters.push({ member, operator: 'equals', values })
    }
  }
  if (widget.type === 'list' || widget.type === 'aging') {
    filters.push(...buildColumnFilters(widget.fields, state.columns))
  }
  filters.push(
    ...selectionFilters(state.selections, {
      key: widget.key,
      view: widget.view,
      fields: widget.fields,
      receive: widget.crossFilter.receive,
      ignored: widget.ignored,
    }),
  )
  return filters
}

/**
 * İki ucu belli sayfa tarih aralığı, gruplu sorgularda `timeDimensions.dateRange` olarak
 * gider: Cube rollup'ı (ör. `TartimBase.tonajGunluk`) yalnız bu biçimde eşliyor; aynı aralık
 * `filters`'ta verilince sorgu kaynak veritabanına gidiyordu. Anlam aynı (uçlar dahil, İstanbul
 * günü). Tek uçlu aralık ve yalnız üst sınırlı filtre (`upperBoundOnly`) eskisi gibi filtre kalır.
 */
function pageDateRange(
  widget: DashboardWidget,
  state: ReportFilterState,
): { dimension: string; dateRange: [string, string] } | undefined {
  const defs = widget.filters.filter((filter) => filter.type === 'dateRange')
  if (!state.dateFrom || !state.dateTo || defs.length !== 1 || defs[0]!.upperBoundOnly) {
    return undefined
  }
  return { dimension: defs[0]!.member, dateRange: [state.dateFrom, state.dateTo] }
}

/** Gruplu parça sorgusunun filtreleri ve (varsa) tarih aralığı boyutu. */
function groupedScope(widget: DashboardWidget, state: ReportFilterState) {
  const range = pageDateRange(widget, state)
  return {
    filters: widgetFilters(widget, state, { skipDateRange: range !== undefined }),
    ...(range && { timeDimensions: [range] }),
  }
}

export function kpiQuery(widget: KpiWidget, state: ReportFilterState): Query {
  return buildCubeQuery({ measures: [widget.measure], ...groupedScope(widget, state) })
}

function order(items: ReportSortItem[]): Query['order'] {
  return items.map((item): [string, 'asc' | 'desc'] => [item.member, item.desc ? 'desc' : 'asc'])
}

export function groupedTableQuery(widget: TableWidget, state: ReportFilterState): Query {
  const extra = currencyMembers(widget.fields, [...widget.dimensions, ...widget.measures])
  return buildCubeQuery({
    dimensions: [...new Set([...widget.dimensions, ...extra])],
    measures: widget.measures,
    ...groupedScope(widget, state),
    order: order(widget.order),
  })
}

/** Toplam satırı: aynı filtreler, gruplama yalnızca döviz üyesine göre. */
export function totalsQuery(
  widget: TableWidget | ListWidget,
  measures: string[],
  state: ReportFilterState,
): Query {
  return buildCubeQuery({
    dimensions: currencyMembers(widget.fields, measures),
    measures,
    // Liste parçasının toplamı da gruplu sorgu; tarih aralığı aynı biçimde gider.
    ...groupedScope(widget, state),
  })
}

/** `ungrouped` + sayfalama; tablo raporuyla aynı desen (`tablePageQuery`). */
export function listPageQuery(
  widget: ListWidget,
  state: ReportFilterState,
  sort: ReportSortItem[],
  page: number,
  pageSize: number,
): Query {
  const columns = widget.dimensions.map((field) => field.name)
  // Ölçü sütunu (KPI kaynak satırlarında kartın limiti gibi): `ungrouped`'da satır başına değer.
  const measures = widget.dimensions.filter((field) => field.aggType).map((field) => field.name)
  const dimensions = columns.filter((name) => !measures.includes(name))
  return buildCubeQuery({
    dimensions: [...new Set([...dimensions, ...currencyMembers(widget.fields, columns)])],
    ...(measures.length > 0 && { measures }),
    filters: widgetFilters(widget, state),
    order: buildTableOrder(widget, sort),
    limit: pageSize,
    offset: (page - 1) * pageSize,
    ungrouped: true,
    total: true,
  })
}

/** Yaşlandırma: süzülen tüm kayıtlar ham satır olarak; dilimleme ve sıralama istemcide. */
export function agingQuery(widget: AgingWidget, state: ReportFilterState): Query {
  const columns = [
    ...widget.dimensions.map((field) => field.name),
    widget.rowKey,
    widget.date,
    widget.amount,
  ]
  return buildCubeQuery({
    dimensions: [...new Set([...columns, ...currencyMembers(widget.fields, [widget.amount])])],
    filters: widgetFilters(widget, state),
    order: [[widget.date, 'asc']],
    limit: EXPORT_ROW_LIMIT,
    ungrouped: true,
  })
}

export function chartWidgetQuery(widget: ChartWidget, state: ReportFilterState): Query {
  // Kategori ekseni: boyuta (ve kırılıma) göre gruplu; sıralama istemcide (`chartBuckets`).
  if (!widget.time) {
    return buildCubeQuery({
      measures: widget.series.map((series) => series.measure),
      dimensions: [widget.category as string, ...(widget.breakdown ? [widget.breakdown] : [])],
      ...groupedScope(widget, state),
    })
  }
  const time = widget.time
  // Sayfa aralığı grafiğin kendi zaman boyutundaysa oraya yazılır (sabit `range` yoksa).
  const page = widget.range ? undefined : pageDateRange(widget, state)
  const merged = page?.dimension === time
  return buildCubeQuery({
    measures: widget.series.map((series) => series.measure),
    ...(widget.breakdown && { dimensions: [widget.breakdown] }),
    timeDimensions: [
      {
        dimension: time,
        granularity: widget.granularity,
        ...(widget.range && { dateRange: widget.range }),
        ...(merged && { dateRange: page.dateRange }),
      },
      ...(page && !merged ? [page] : []),
    ],
    filters: widgetFilters(widget, state, { skipDateRange: page !== undefined }),
    order: [[time, 'asc']],
  })
}
